import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AppPreference, DEFAULT_APP_PREFERENCE } from '../../models/settings/employee-settings.model';

@Injectable({ providedIn: 'root' })
export class EmployeeSettingsService {
  private readonly http = inject(HttpClient);
  private readonly keyPrefix = 'trieonhr_app_preference';
  private loadedFor = '';

  readonly compactMode = signal(false);
  readonly collapseSidebarByDefault = signal(false);
  readonly workspaceRevision = signal(0);

  load(userProfileGuid: string): Observable<AppPreference> {
    this.applyCached(userProfileGuid);
    return this.http
      .get<{ userAppPreference: AppPreference }>(this.url(userProfileGuid), { withCredentials: true })
      .pipe(map(res => ({ ...DEFAULT_APP_PREFERENCE, ...res.userAppPreference })))
      .pipe(tap(preference => {
        this.remember(userProfileGuid, preference);
        this.loadedFor = userProfileGuid;
      }));
  }

  ensureLoaded(userProfileGuid: string): void {
    if (!userProfileGuid) return;
    this.applyCached(userProfileGuid);
    if (this.loadedFor === userProfileGuid) return;
    this.load(userProfileGuid).subscribe({ error: () => undefined });
  }

  save(userProfileGuid: string, preference: AppPreference): Observable<AppPreference> {
    return this.http
      .put<{ userAppPreference: AppPreference }>(this.url(userProfileGuid), preference, { withCredentials: true })
      .pipe(map(res => ({ ...DEFAULT_APP_PREFERENCE, ...res.userAppPreference })))
      .pipe(tap(saved => {
        this.remember(userProfileGuid, saved);
        this.loadedFor = userProfileGuid;
      }));
  }

  private applyCached(userProfileGuid: string): void {
    const raw = localStorage.getItem(this.buildKey(userProfileGuid));
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Partial<AppPreference>;
      this.compactMode.set(!!parsed.compactMode);
      this.collapseSidebarByDefault.set(!!parsed.collapseSidebarByDefault);
      this.workspaceRevision.update(n => n + 1);
    } catch {
      localStorage.removeItem(this.buildKey(userProfileGuid));
    }
  }

  private remember(userProfileGuid: string, preference: AppPreference): void {
    this.compactMode.set(preference.compactMode);
    this.collapseSidebarByDefault.set(preference.collapseSidebarByDefault);
    this.workspaceRevision.update(n => n + 1);
    if (userProfileGuid) {
      localStorage.setItem(this.buildKey(userProfileGuid), JSON.stringify(preference));
    }
  }

  private url(userProfileGuid: string): string {
    return `${environment.user.root}/${userProfileGuid}/app-preference`;
  }

  private buildKey(userProfileGuid: string): string {
    return `${this.keyPrefix}_${userProfileGuid}`;
  }
}
